// Copy for each vertical page. Figures come from vimbal's decks (mining pilot) or cited public sources.
// No individuals are referenced on these pages by design.

import videoNeuro from "../vid/vimbal_video_three.mp4";
import videoWork from "../vid/vimbal_induction_site.mp4";
import videoMilitary from "../vid/vimbal_military.mp4";
import videoWoman from "../vid/vimbal_neuro_woman.mp4";
import videoStudy from "../vid/vimbal_study_laptop.mp4";
import { colors } from "../styles/theme";
import { ndisVertical } from "./ndisContent";

// Mining pilot results, from vimbal's safety deck. avg / best-case per group.
// Australian workplace induction pilot, 2024. Twelve participants with complete data
// (a thirteenth, ESL, did not complete every round and is excluded). Four rounds each:
// one timed paper baseline, then three in vimbal, on the same induction material.
//
// Two tabs because the honest answer depends on what you measure against. Tab one is the
// rigorous within-subject result, each person against their own baseline. Tab two exists
// because that baseline is inflated: participants knew they were being timed and read
// competitively, so their "before" speed was faster than they would ordinarily read.
export const pilotResults = {
  label: "Measured on site, Australian engineering and mining services",
  note: "Each person read the same induction on paper, then in vimbal, answering the same test after every round. Test scores averaged 65.3% on paper and 81.3% in vimbal, a bare credit lifted to a distinction. Half the group scored below a credit on the paper round. In vimbal, none did, and more than a third reached high distinction. Every participant read faster in vimbal.",
  groups: [
    {
      key: "measured",
      tab: "Measured on site",
      rows: [
        { metric: "Test score", avg: 29, best: 70, dir: "up" },
        { metric: "Induction speed", avg: 38, best: 66, dir: "up" },
        { metric: "Time per induction", avg: 27, best: 40, dir: "down" },
      ],
    },
    {
      key: "benchmark",
      tab: "Induction speed against published reading rates",
      rows: [
        // Largest first: a reader who stops after one row should get the biggest number.
        { metric: "vs adults with low literacy, 50 to 70 wpm", avg: 406, best: 550, dir: "up" },
        { metric: "vs technical material, under 150 wpm", avg: 136, best: 203, dir: "up" },
        { metric: "vs reading to learn, 200 wpm", avg: 77, best: 128, dir: "up" },
      ],

    },
  ],
};

// The results card shows the benchmark comparison only. The measured figures are already
// in the hero stats and in the note under the table, so a second tab repeated them.
export const pilotResultsBenchmarkOnly = {
  ...pilotResults,
  groups: pilotResults.groups.filter((g) => g.key === "benchmark"),
};

// Anonymised pilot testimonials. `pages` lists where each one appears (plus the overview).
// `results` is each person's own pilot outcome. It is kept as a record but no longer
// rendered: small individual percentages undersold the quotes they sat beside, so the
// numbers are now carried only by the aggregate pilot results above.
export const testimonials = {
  eyebrow: "In their words",
  titleLead: "What it feels like",
  titleEmphasis: "from the inside.",
  disclaimer: "Anonymized participants from vimbal's on-site testing.",
  people: [
    {
      key: "researcher",
      who: "PhD neuroscientist with ADHD",
      detail: "Medical researcher of 17 years, 45",
      quote: [
        "The focus was the most incredible thing for me. I was able to retain attention. That was huge for me.",
        "Focusing on a task can take a Herculean effort, and that's why it felt strange to me. My brain was behaving differently. I didn't know what to make of it.",
        "The only other time I felt something similar was the very first time I took ADHD meds. That sort of mental clarity.",
      ],
      pull: "The only other time I felt something similar was the very first time I took ADHD meds.",
      results: { speed: 121, retention: 31, grade: 19 },
      pages: ["neurodivergent", "education", "mental-health"],
    },
    {
      key: "theology",
      who: "Neurodivergent adult with a master's degree in theology",
      detail: "13 diagnoses, including autism, ADHD, anxiety, depression and PTSD",
      quote: [
        "I read 5,200 words of theological material in 14.5 minutes. Usually 5,200 words would take two days of reading and note taking.",
        "My anxiety is usually 7 to 8 out of 10. In vimbal it was 1 to 2. Afterwards I was 3.",
      ],
      pull: "Usually that would take two days of reading and note taking.",
      results: { speed: 40, retention: 50, grade: 27 },
      pages: ["neurodivergent"],
    },
    {
      key: "forklift-driver",
      who: "Forklift driver",
      detail: "50 · left school at 14 · moved to tears by the quiet of a first induction session",
      quote: [
        "It seems a bit weird to me that my brain is able to have this much concentration, and absorb all this.",
        "You've triggered a part in my brain that would actually be interested in seeing how far this goes. What can it do? What can you learn from it? What can you teach yourself, and how much am I really absorbing?",
        "Maybe I don't have to live like that anymore. Maybe this sort of technology can actually help me, and enable me to step forward.",
      ],
      pull: "Maybe I don't have to live like that anymore.",
      results: { speed: 32, retention: 33, grade: 16 },
      pages: ["workplace-safety", "mental-health"],
    },
  ],
};

// Voices from the workplace induction sessions. Tradespeople and engineers,
// each with years of inductions behind them. De-identified to role and years only: no
// names, no employer, no client sites. The "fix" column is deliberate: the pilot found
// real faults, and a page that only shows praise reads like a page that hid the rest.
export const pilotVoices = {
  eyebrow: "Workforce response",
  titleLead: "What the workforce",
  titleEmphasis: "said afterwards.",
  intro:
    "Tradespeople and engineers with careers of site inductions behind them. We asked how inductions normally go, ran the same material through vimbal, and asked again. Responses are verbatim.",
  beforeTitle: "Inductions, as they are",
  afterTitle: "The same person, afterwards",
  // Each row is one person, before and after. They line up deliberately: the point is not
  // that some people dislike inductions and other people liked vimbal, it is that the same
  // person said both things half an hour apart.
  pairs: [
    {
      who: "QA/QC engineer, 18 years",
      before:
        "It's lengthy. It takes a full day at a time. Sometimes you simply sign, because we need to complete it in one day.",
      after:
        "Once you see the questions, the key word comes to your mind. You have seen this one, you have listened to these words. It helps you answer quickly.",
    },
    {
      who: "Poly welder",
      before: "It goes in this ear and out this ear, because it's just a bombardment.",
      after:
        "It seemed to flow easier. Previously you'd go, I remember seeing something like that, but I've not retained any of that information. This was: oh yeah, that's the answer, that's the answer, that's the answer.",
    },
    {
      who: "Engineer",
      before: "You're reading a document, and by the end of it you're just zoned out and thinking about other things.",
      after:
        "The only thing I can really focus on is the words and the sounds. It's easier to concentrate as you take it in. It definitely took less of a toll on me than a normal induction would.",
    },
    {
      who: "Trades assistant, 24",
      before: "Here's a set of papers, work your way through it, and you're done. Lots and lots of paperwork.",
      after: "A lot less distractions. It felt quicker, easier, and I felt more focused.",
    },
    {
      who: "Boilermaker, 16 years in the trade",
      before: "I'll be honest, I fell asleep through a couple.",
      after: "I'd like to do it that way again, just to see if it makes any difference how I take it in.",
    },
    {
      who: "Project engineer, English as a second language",
      before: "I'm the textbook guy. Reading is no problem.",
      after: "Reading this way was easier than reading the textbook. When you listen, you can add up more things.",
    },
    {
      who: "IT manager, 20 years",
      before:
        "It's just like going to an exam. When you're supposed to absorb information and then be examined on it, that can put you in a stressful situation.",
      after:
        "I felt relaxed, and comfortable answering the questions. I didn't feel stressed. My short-term memory is not that great. Immediate retention on something I've just read is normally not good. But I think I did okay.",
    },
  ],
  closingQuote:
    "If you want to make sure that people have actually done the thing, then vimbal is a great system.",
  closingWho: "Boilermaker and rubber liner, 50 inductions across 20 sites",
  note: "Measured on site with an Australian engineering and mining services company. Participants are de-identified and quoted with permission.",
};

// The brain chemistry behind distraction (from the US defence deck, condensed).
export const chemistry = [
  { name: "Dopamine", role: "The seeker", body: "Spikes in anticipation of something new. Constant pings and scrolling build tolerance, so the brain craves novelty but never feels satisfied." },
  { name: "Cortisol", role: "The stress alarm", body: "Released under stress and uncertainty. Kept high for too long, it weakens memory and shortens attention. A brain scanning for danger can't focus." },
  { name: "Norepinephrine", role: "The gas pedal", body: "Sharpens focus in short bursts. Triggered constantly by alerts and multitasking, it burns out and attention turns jumpy." },
  { name: "Serotonin", role: "The mood balancer", body: "Helps you feel calm and secure. Comparison and online stress knock it down, and low levels are linked to anxiety and low mood." },
  { name: "GABA", role: "The calmer", body: "The brain's brake system, quieting the chatter so you can think. Chronic stress and overstimulation suppress it." },
  { name: "Acetylcholine", role: "The laser", body: "The focus master that locks you in. It's easily drowned out by dopamine-chasing and background stress, so the mind flickers instead of flows." },
];

export const verticals = [
  {
    slug: "neurodivergent",
    nav: "Neurodivergent",
    accent: colors.ocean,
    eyebrow: "Neurodivergence",
    titleLead: "Different minds.",
    titleEmphasis: "Frictionless learning.",
    body:
      "Learning material is designed for average attention. vimbal takes the material you already have and delivers it in a stimulation-free space, where ADHD, autistic and dyslexic learners set the type, color, sound and pace.",
    video: videoWoman,
    cta: "Arrange a session",
    // Two boxes, aimed at a parent rather than an investor: one outcome they can picture,
    // one answer to the question they arrive with. Prevalence figures sized a market and
    // did nothing for the reader, so they came out of the hero.
    heroFigures: [
      { value: "14 minutes", label: "to read what usually took one reader two days" },
      { value: "Non‑invasive", label: "no medication and no implants, just a headset" },
    ],
    problemTitle: "When the alarm never switches off",
    problems: [
      { title: "An over-stimulated stress center", body: "A highly stimulated amygdala keeps the brain in fight-or-flight, and very little information makes it through to memory." },
      { title: "Noise everywhere", body: "Notifications, sound and visual clutter pull attention off the page before a paragraph is finished." },
      { title: "Reading as a battle", body: "Stopping, re-reading and note-taking turn a short document into days of work." },
    ],
    quote: "The neurodivergent mind is a superpower. vimbal unleashes it.",
    note: "A participant read 5,200 words in 14.5 minutes, and recalled it unprompted afterwards.",
    howTitle: "Designed around neurodivergent minds",
    how: [
      { title: "Stimulation-free space", body: "An immersive environment removes outside distractions, so there's only you and the words." },
      { title: "Neurodivergent modes", body: "Dyslexia-friendly fonts, contrast overlays and ADHD pacing, with a color palette every tester gravitated to." },
      { title: "Sound and motion", body: "Audio synced to your reading pace, and vimbal's rhythmic motion patterns, keep the mind steady." },
      { title: "Your pace, rising", body: "Start comfortably and dial the speed up as confidence grows." },
    ],
    calculator: {
      title: "How long would it take you?",
      words: 5200,
      wordsLabel: "Words to read (a 19-page document)",
      typical: 110,
      vimbal: 350,
    },
    uses: ["Primary and secondary school", "University study", "Workplace training", "Learning for passion"],
    delivery:
      "Coming to the learning platforms schools and workplaces already use, with university research planned into literacy and neurodivergence.",
  },
  {
    slug: "mental-health",
    nav: "Mental Health",
    accent: colors.moss,
    eyebrow: "Mental health",
    titleLead: "Quiet the alarm.",
    titleEmphasis: "Make room to grow.",
    body:
      "Anxiety, depression and PTSD can keep the brain's stress center on high alert, and while the amygdala is on guard, very little reaches memory. vimbal guides people from distraction to deep focus in under three minutes, then lets learning begin.",
    video: videoNeuro,
    cta: "Arrange a session",
    breathing: true,
    heroFigures: [
      { value: "7–8 → 1–2", label: "one participant's own anxiety rating, during a session" },
      { value: "< 3 minutes", label: "from distraction to deep, calm focus" },
    ],
    problemTitle: "Stress closes the door to memory",
    problems: [
      { title: "Stuck in fight-or-flight", body: "When the amygdala is over-stimulated, the brain prioritizes threat over learning, and information struggles to reach the hippocampus." },
      { title: "Sessions spent getting calm", body: "Much of a session can go on settling someone before any new strategy or exercise can start." },
      { title: "Always on", body: "Constant notifications and scrolling keep stress hormones high and calming chemistry low." },
    ],
    quote: "You can see the moment the stress response lets go: shoulders release, hands drop, the head tilts, breathing slows.",
    note: "Observed across vimbal sessions.",
    howTitle: "A guided path to calm",
    how: [
      { title: "Break the current state", body: "Audio-visual immersion lifts you out of the moment you were stuck in." },
      { title: "Settle into the scene", body: "A meditative environment, like diving on the Great Barrier Reef, brings you into the present." },
      { title: "Deep calm", body: "Calm arrives quickly and the body settles as the neuroscience combines, taking you deeper." },
      { title: "Convergence point", body: "Neurological sound, color, motion, and pace, all arrive together. The point vimbal is built for." },
      { title: "Flow state", body: "The brain slows, breathing slows, shoulders drop, and the information reaches you. More than ten grown men have cried in the workplace at this point." },
      { title: "Learn from calm", body: "You come out able to speak about the material, and you score higher when tested on it." },
    ],
    research:
      "vimbal is pursuing independent university research into mental health, PTSD and neurodivergence, working towards Software as a Medical Device status.",
    uses: ["Stress and anxiety", "Neuro-rehabilitation", "Meditation and flow state", "Veterans", "Support organizations and advocacy groups"],
    delivery: "Designed to sit alongside professional care, never replace it.",
  },
  ndisVertical,
  {
    slug: "education",
    nav: "Education",
    accent: colors.copper,
    eyebrow: "Education and literacy",
    titleLead: "Every student,",
    titleEmphasis: "at their best pace.",
    body:
      "Almost one in eight domestic students, and one in six international students, leave in their first year. A new language, new material and a heavier workload arrive at once, and a student who falls behind rarely catches up. vimbal turns learning into a meditative, calm, focused flow state, so students keep pace and recover ground.",
    video: videoStudy,
    cta: "Arrange a demonstration",
    chemistry: true,
    heroFigures: [
      { value: "65% \u2192 81%", label: "test scores in vimbal, measured on adult learners" },
      { value: "354 wpm", label: "measured reading speed, against 200 wpm for reading to learn" },
    ],
    statsNote: "Test scores and reading speed measured on site with adult learners, on their own material. Attrition figures: Australian Department of Education, 2023 commencing cohort. Education studies are planned.",
    problemTitle: "Built to distract, expected to focus",
    problems: [
      { title: "The literacy gap", body: "Too many students leave school unable to read at the level their next step demands." },
      { title: "Distraction by design", body: "Devices are engineered to chase attention, leaving little room for deep, focused reading." },
      { title: "First-semester drop-out", body: "A new vocabulary, new material and a heavier workload arrive in the same weeks. Ground lost there is rarely recovered." },
    ],
    quote: "The brain needs to be in a state to learn. vimbal puts it there first.",
    howTitle: "From any document to deep understanding",
    how: [
      { title: "Upload anything", body: "Add a PDF through the web app and it's ready to read in the headset." },
      { title: "Get into the state", body: "Guided immersion takes students from scattered to deeply focused in under three minutes." },
      { title: "Read at your speed", body: "Speed rises with confidence, with type, color and sound tuned for focus." },
      { title: "Remember it", body: "Less stress means a clearer path to memory, and better recall when it counts." },
    ],
    calculator: {
      title: "What could a semester look like?",
      words: 60000,
      wordsLabel: "Words of reading per week",
      typical: 200,
      vimbal: 539,
    },
    uses: ["Primary and secondary school", "Tertiary and university", "College", "Workplace education", "Micro-credentialling", "Literacy programs"],
    delivery: "Integration with learning management systems is on the roadmap, bringing vimbal to the tools schools already use.",
  },
  {
    slug: "workplace-safety",
    nav: "Workplace & Safety",
    accent: colors.terracotta,
    eyebrow: "Workplace and safety",
    titleLead: "Safer workers.",
    titleEmphasis: "Faster onboarding.",
    body:
      "Inductions are too often 'tick and flick'. vimbal ensures every word is read and remembered, so safety teams can include more critical content in less time, to send people home safe.",
    video: videoWork,
    cta: "Arrange a site session",
    // Two figures in the hero to stop the reader. The quad below proves them.
    heroFigures: [
      { value: "\u2193 40%", label: "less time per induction, at best" },
      { value: "\u2191 24.5%", label: "higher safety induction scores, on their own material" },
    ],
    pilot: true,
    problemTitle: "Tick and flick is a safety risk",
    problems: [
      { title: "Nobody reads the induction", body: "Slides get clicked through, and critical safety information never sticks." },
      { title: "Expensive onboarding", body: "A week of inductions per worker, crews flown in early for shutdowns, and costly drop-outs during probation." },
      { title: "The content that gets left out", body: "HSEQ would add more: the full procedure, the near-miss history, the lessons paid for the hard way. It gets cut, because everyone knows a longer pack won't be read." },
    ],
    quote: "Nobody wants to knock on a family's door with bad news. Retention is what keeps people safe.",
    howEyebrow: "In practice",
    howTitle: "What a session involves",
    how: [
      { title: "The hardware", body: "A headset and noise-cancelling headphones per person. Sessions run to 40 minutes." },
      { title: "Your own material", body: "Upload the PDFs you already use. Inductions, SOPs, manuals and procedures, unchanged." },
      { title: "Setup", body: "A few minutes to fit the headset and walk through the controls, then straight into the document." },
      { title: "How the reading works", body: "Select the document from the library. Sit back, turn up the speed, and let the neuroscience work around you." },
    ],
    costCalculator: true,
    // Three pairs, not seven: the argument lands by the third and the rest repeated it.
    pilotVoices: 3,
    // The evidence block: two results in dark cards, speed comparison and savings beneath.
    resultsQuad: {
      eyebrow: "Measured on site",
      titleLead: "What happened when",
      titleEmphasis: "we ran it.",
      intro:
        "We ran it inside an Australian engineering and mining services company of several hundred staff, on their own safety induction material. Tradespeople and engineers, most of whom left school before Year 12.",
      headlines: [
        {
          title: "More content in less time",
          value: "\u2193 40%",
          body:
            "Inductions took up to 40% less time. Cost falls with the hours.",
          note: "Induction time, measured on site · cost follows the hours",
        },
        {
          title: "A credit becomes a distinction",
          value: "65% \u2192 81%",
          body: "Half the group scored below a credit beforehand. Afterwards, not one of them did.",
          note: "Safety induction test scores · more than a third reached high distinction",
        },
      ],
      speed: {
        title: "Induction speed",
        value: "354 wpm",
        under: "group average, on their own induction material",
        rows: [
          { multiple: "5\u00d7", label: "an adult with low literacy", rate: "50 to 70 wpm" },
          { multiple: "2.4\u00d7", label: "the rate for technical material", rate: "under 150 wpm" },
          { multiple: "1.8\u00d7", label: "reading in order to learn", rate: "200 wpm" },
        ],
        note: "Low literacy is the risk on any induction: the worker who cannot get through the pack signs it anyway. The fastest read 6.5× the published low-literacy rate.",
      },
    },

    // Ordered by induction burden. The last three are situations rather than industries,
    // and they are where the pain actually sits: the same worker inducted onto many sites,
    // a surge crew to induct at once, and an unchanged pack re-read every twelve months.
    uses: [
      "Mining and resources",
      "Oil, gas and processing",
      "Power, utilities and transmission",
      "Rail and heavy transport",
      "Construction and civil infrastructure",
      "Ports, terminals and marine",
      "Heavy manufacturing",
      "Shutdowns and turnarounds",
      "Labor hire and multi-site contracting",
      "Refresher and recurrent training",
    ],
    delivery:
      "Already in use with paying clients in Australian mining. Upload SOPs, manuals and training protocols as PDFs today, with more formats on the way, and deliver them on XR headsets or the web.",
  },
  {
    slug: "military",
    nav: "Defense",
    accent: colors.forest,
    dark: true,
    eyebrow: "Defense",
    titleLead: "Accelerate",
    titleEmphasis: "warfighter readiness.",
    body:
      "Service members are high-stress and time-poor, and stress blocks retention. vimbal takes people from distraction to cognitive flow in under five minutes, then accelerates the learning that matters.",
    video: videoMilitary,
    // Portrait footage in wide frames: keep the face in shot.
    videoPosition: "center 22%",
    cta: "Arrange a briefing",
    heroFigures: [
      { value: "< 5 minutes", label: "from high stress to cognitive flow" },
      { value: "1 in 4", label: "high school graduates miss the Army's entry score" },
    ],
    problemTitle: "High stress, little time, low retention",
    problems: [
      { title: "Always on", body: "Long duty days, family demands and constant pressure keep stress hormones high." },
      { title: "Limited study time", body: "Courses and certifications compete with operational demands, and study hours are scarce." },
      { title: "Pass or recycle", body: "Must-meet standards, pass or fail, recycle or return to unit, and the embarrassment that follows: all add stress, and stress cuts how much of every study hour is retained." },
    ],
    quote: "Forces that can learn, adapt and retain mission-critical knowledge under stress.",
    howTitle: "Where vimbal fits",
    how: [
      { title: "Accelerated onboarding", body: "Nearly one in four high school graduates misses the Army's entry score, and the Army now runs a 90-day course to lift them over it. vimbal closes that gap on the recruit's own material." },
      { title: "Foundational training", body: "Classroom basics of equipment and safety, learned faster and remembered better." },
      { title: "Specialist training", body: "From machinery licenses to aviation, with more comprehension from every hour." },
      { title: "Advanced education", body: "Pathways into military engineering, law and medicine." },
      { title: "Downtime and transit", body: "Turn travel and the end of a long day into focused learning time." },
      { title: "Resilience", body: "A calm reset from high-stress environments, with PTSD research planned." },
      { title: "Course and test preparation", body: "Read the material once, calmly, before the exam that decides whether you pass or recycle." },
      { title: "Skill maintenance", body: "Refresher and recurrency material, kept current on the same platform, without a classroom." },
    ],
    calculator: {
      title: "Hours back per course",
      words: 120000,
      wordsLabel: "Words of course material",
      typical: 150,
      vimbal: 539,
    },
    uses: ["Recruitment and onboarding", "Initial training", "Specialist and aviation training", "Professional education", "Veteran support"],
    delivery:
      "vimbal is in discussion with defense innovation channels in the United States and Australia about procedural and readiness training, and can deploy at short notice.",
  },
];


export const verticalBySlug = Object.fromEntries(verticals.map((v) => [v.slug, v]));
